/**
 * Nhóm công đoạn lớn dùng cho trang Kế hoạch sản xuất (/process-planning).
 *
 * Nguồn dữ liệu chính: process_groups.planning_stage (gán qua processes.process_group_id).
 * classifyProcessStage() là quy tắc dự phòng cho công đoạn chưa được gán nhóm, và cũng là
 * quy tắc dùng để bổ sung dữ liệu lần đầu (prisma/migrations-safe/phase262_process_planning_stages.sql
 * dùng cùng các mẫu này — sửa ở đây thì sửa cả file SQL).
 */
export const PLANNING_STAGES = [
  { key: 'CUTTING', label: 'Cắt giấy', groupCode: 'CUTTING' },
  { key: 'PRINTING', label: 'In ấn', groupCode: 'PRINTING' },
  { key: 'LAMINATING', label: 'Cán màng', groupCode: 'LAMINATION' },
  { key: 'COATING', label: 'Tráng phủ', groupCode: 'COATING' },
  { key: 'DIE_FOIL', label: 'Bế - Ép nhũ', groupCode: 'DIE_CUTTING' },
  { key: 'GLUING', label: 'Dán', groupCode: 'GLUING' },
  { key: 'FINISHING', label: 'Thành phẩm', groupCode: 'FINISHING' },
] as const;

export type PlanningStageKey = (typeof PLANNING_STAGES)[number]['key'];

export const PLANNING_STAGE_KEYS = PLANNING_STAGES.map((s) => s.key) as PlanningStageKey[];

/** Công đoạn không thuộc tab sản xuất nào (chỉ hiện ở "Tất cả") */
export const PLANNING_STAGE_EXCLUDED = 'EXCLUDED' as const;

/** Quy tắc theo tên, xét theo thứ tự (cụ thể trước, chung sau) */
const STAGE_RULES: Array<[PlanningStageKey | typeof PLANNING_STAGE_EXCLUDED, RegExp]> = [
  [PLANNING_STAGE_EXCLUDED, /giao hàng|vận chuyển|thiết kế|quy trình mới/i],
  ['PRINTING', /^in\b|\bin (offset|flexo|lụa|màu|[1-6])|cmyk|màu pha|kéo lụa|^\d{3,4}$|hero blue/i],
  ['CUTTING', /cắt xén|xén giấy|cắt giấy|cắt biên/i],
  ['FINISHING', /cắt thành phẩm|gấp|đóng gói|đóng thùng|kcs|kiểm|xé hộp|xỏ dây|quấn cuộn|ép 3 biên|gia công thành phẩm|tề túi/i],
  ['DIE_FOIL', /bế|ép kim|ép nhũ|nổi|chiết quang|đóng mặt cao/i],
  ['GLUING', /dán|bồi/i],
  ['LAMINATING', /cán|ghép màng|ghép metalize|giựt màng/i],
  ['COATING', /phủ|vecni|vanish|\buv\b|water base|tráng/i],
];

/** Nhóm cũ (processes.group) khi tên không khớp quy tắc nào */
const LEGACY_GROUP_FALLBACK: Record<string, PlanningStageKey> = {
  IN: 'PRINTING',
  OFFSET_PRINTER: 'PRINTING',
  LAMINATOR: 'LAMINATING',
};

export function classifyProcessStage(
  name: string | null | undefined,
  legacyGroup?: string | null,
): PlanningStageKey | typeof PLANNING_STAGE_EXCLUDED | null {
  const text = (name || '').trim();
  for (const [stage, re] of STAGE_RULES) if (re.test(text)) return stage;
  return (legacyGroup && LEGACY_GROUP_FALLBACK[legacyGroup]) || null;
}

export function isPlanningStageKey(v: unknown): v is PlanningStageKey {
  return typeof v === 'string' && (PLANNING_STAGE_KEYS as string[]).includes(v);
}

/** Bộ lọc trạng thái trên trang Kế hoạch sản xuất (gộp trạng thái công đoạn thành nhóm người dùng hiểu) */
export const PLANNING_STATUS_FILTERS = [
  { key: 'WAITING', label: 'Chờ chạy', statuses: ['PLANNED', 'READY', 'SCHEDULED'] },
  { key: 'RUNNING', label: 'Đang chạy', statuses: ['RUNNING'] },
  { key: 'PAUSED', label: 'Tạm dừng', statuses: ['PAUSED'] },
  { key: 'COMPLETED', label: 'Hoàn thành', statuses: ['COMPLETED'] },
  { key: 'OVERDUE', label: 'Quá hạn', statuses: [] as string[] },
] as const;

export type PlanningStatusFilterKey = (typeof PLANNING_STATUS_FILTERS)[number]['key'];
